import { memo } from 'react';
import { ReactComponent as Tag } from '@assets/icons/tag.svg';
import Image from 'next/image';

import { Box, Flex, Stack, useBoolean, useTheme } from '@chakra-ui/react';

import { formatCurrency } from 'utils';

import {
  CartBasketItem,
  CartItemOnlineFulfillmentEnum,
  UserFulfillmentMode
} from 'types';

import { FulfillmentSelector } from 'components/FulfillmentSelector';
import { Loader, Text } from 'ui';

interface CartItemProps extends CartBasketItem {
  isTagDisabled: boolean;
  isShowTag: boolean;
  onFulfillmentChange: (mode: UserFulfillmentMode) => void;
}

const CartItem = ({
  id,
  name = '',
  size = '',
  img = '',
  newItemPrice = 0,
  oldItemPrice = 0,
  priceWithDiscount = 0,
  isShowTag,
  isTagDisabled,
  qty = 1,
  onFulfillmentChange,
  online_fulfillment
}: CartItemProps) => {
  const {
    colors: {
      palette: {
        components: { cartBasket: styles }
      }
    }
  } = useTheme();
  const [loading, setLoading] = useBoolean(true);

  const isMixedDeliveryAvailable =
    online_fulfillment === CartItemOnlineFulfillmentEnum.ShippingAndPickup;

  const handleChangeMode = (mode: UserFulfillmentMode) => {
    onFulfillmentChange(mode);
  };

  return (
    <Stack
      gap={0}
      w='full'
      p={4}
      bg={styles.itemBg}
      borderRadius='3px'
      overflow='hidden'
      shadow='sm'
      h='full'
      flex={1}
      _hover={{
        shadow: 'md'
      }}
      sx={{
        transition: 'all 0.5s'
      }}
      alignItems='stretch'
    >
      <Flex gap={0} w='full' flex={1} alignItems='stretch'>
        <Box w={{ base: '120px' }} position='relative'>
          {img && !img.includes('tag.png') ? (
            <Flex
              alignItems='center'
              justifyContent='center'
              bg='white'
              h='120px'
              w='120px'
              borderRadius='3px'
              overflow='hidden'
            >
              {loading && <Loader absolute />}
              <Image
                src={img}
                quality={100}
                sizes='(max-width: 450px) 150px, 200px'
                height={120}
                width={120}
                style={{
                  objectFit: 'contain',
                  aspectRatio: '1/1'
                }}
                alt={`Shopping Image ${name}`}
                onLoad={setLoading.off}
                priority
              />
            </Flex>
          ) : (
            <Flex
              alignItems='center'
              justifyContent='center'
              my='auto'
              position='relative'
              h='120px'
              w='120px'
              bg={styles.emptyImageBg}
              borderRadius='3px'
              overflow='hidden'
            >
              <Tag width={60} height={60} fill={styles.defaultIconColor} />
            </Flex>
          )}
        </Box>
        <Flex
          flexDirection='column'
          bg={styles.productBg}
          justifyContent='space-between'
          w='full'
          pl={{ base: '12px', sm: '18px' }}
          pr={{ base: '12px', sm: 0 }}
          flex={1}
          gap={0}
          alignItems='stretch'
        >
          <Flex
            flexDirection='column'
            flex={1}
            w='full'
            cursor='default'
            pb='14px'
          >
            <Box>
              <Text
                fontSize={{ base: '16px', lg: '18px' }}
                color={styles.itemNameColor}
                textAlign='left'
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical'
                }}
              >
                {name}
              </Text>
              {size && (
                <Text
                  mt='4px'
                  fontSize={{ base: '12px', md: '14px' }}
                  color={styles.itemSizeColor}
                  textAlign='left'
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical'
                  }}
                >
                  {size}
                </Text>
              )}
            </Box>

            <Flex
              flexDirection='column'
              alignItems='flex-start'
              justifyContent={{
                base: 'flex-end',
                sm: newItemPrice !== oldItemPrice ? 'flex-end' : 'center'
              }}
              flex={1}
            >
              <Text
                fontSize='18px'
                color={styles.mainPriceColor}
                textAlign='left'
                pt='10px'
              >
                {`${formatCurrency(newItemPrice)} ea`}
              </Text>

              {newItemPrice !== oldItemPrice && (
                <Text
                  pt='6px'
                  fontSize='15px'
                  color={styles.discountPrice}
                  textAlign='left'
                  textDecoration='line-through'
                >
                  {formatCurrency(oldItemPrice)}
                </Text>
              )}
            </Flex>
          </Flex>

          {isShowTag && (
            <Flex w='100%' justifyContent='flex-start' mb='8px' mt='-7px'>
              <FulfillmentSelector
                defaultMode={
                  online_fulfillment ||
                  CartItemOnlineFulfillmentEnum.ShippingAndPickup
                }
                cartItemId={id}
                isReadOnly={isTagDisabled}
                isDisplayMenu={isMixedDeliveryAvailable}
                onModeChange={handleChangeMode}
              />
            </Flex>
          )}

          <Flex
            w='full'
            pt='10px'
            justifyContent='space-between'
            alignItems='center'
            borderColor={styles.borderColor}
            borderTopWidth={1}
          >
            <Text
              fontSize='18px'
              color={styles.mainPriceColor}
              textAlign='left'
              pr={2}
              py={1}
            >
              {`QTY: ${qty}`}
            </Text>
            <Flex alignItems='center' gap='16px'>
              {/* {totalWithAllDiscount !== priceWithDiscount && (
              <Text
                fontSize='16px'
                color={styles.discountPrice}
                textAlign='left'
                textDecoration='line-through'
              >
                {formatCurrency(totalWithAllDiscount)}
              </Text>
            )} */}

              <Text
                fontSize='22px'
                color={styles.mainPriceColor}
                textAlign='left'
              >
                {formatCurrency(priceWithDiscount)}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Stack>
  );
};

export default memo(CartItem);
